import { useMemo, useState } from 'react'

import { useSelector } from 'react-redux'

import { FeedbackType } from '@/core/api/entities/song/feedbackType'
import useTranslation from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import { Button } from '@/shared/components/atoms/Button'
import { Label } from '@/shared/components/atoms/Label/Label'
import { RadioGroup, RadioGroupItem } from '@/shared/components/atoms/Radio'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

interface ChooseNegativeFeedbackModalProps {
	showNegativeFeedbackOptionsModal: boolean
	onClose: () => void
	onConfirm: (feedbackTypeId: number) => void
	feedbackTypes: FeedbackType[]
}

export const ChooseNegativeFeedbackModal = ({
	showNegativeFeedbackOptionsModal,
	onClose,
	onConfirm,
	feedbackTypes,
}: ChooseNegativeFeedbackModalProps) => {
	const [selectedNegativeFeedbackTypeId, setSelectedNegativeFeedbackTypeId] =
		useState<number | null>(null)

	const { t } = useTranslation('common')

	const { language } = useSelector((state: RootState) => state.ui)

	const filteredNegativeFeedbackTypes = useMemo(() => {
		return (
			feedbackTypes?.filter((feedbackType) => feedbackType.isNegative) || []
		)
	}, [feedbackTypes])

	return (
		<Dialog
			open={showNegativeFeedbackOptionsModal}
			onOpenChange={(open) => {
				if (!open) {
					onClose()
					setSelectedNegativeFeedbackTypeId(null)
				}
			}}
		>
			<DialogContent className="w-[500px] h-[500px] flex flex-col justify-between">
				<DialogHeader>
					<DialogTitle className="text-lg font-semibold">
						{t('entities.music.why_dont_you_like_this_song')}
					</DialogTitle>
					<DialogDescription className="text-sm text-muted-foreground">
						{t('entities.music.feedback_subtitle')}
					</DialogDescription>
				</DialogHeader>
				<div className="py-4 flex-1">
					{filteredNegativeFeedbackTypes.length === 0 ? (
						<div className="flex items-center justify-center py-8">
							<div className="text-sm text-muted-foreground">
								{t('entities.music.no_feedback_options')}
							</div>
						</div>
					) : (
						<RadioGroup
							value={selectedNegativeFeedbackTypeId?.toString() || ''}
							onValueChange={(value) =>
								setSelectedNegativeFeedbackTypeId(parseInt(value))
							}
							className="space-y-3"
						>
							{filteredNegativeFeedbackTypes.map((feedbackType) => {
								// Get the appropriate translation based on current language
								// Try to match the full language code first, then fall back to base language
								const getTranslatedTitle = () => {
									// Try exact match first
									if (feedbackType.titleTranslations[language]) {
										return feedbackType.titleTranslations[language]
									}

									// Get base language (e.g., 'en' from 'en-US')
									const baseLanguage = language?.split('-')[0]

									// Try to find a matching translation by base language
									// Priority: exact match > es-ES/es-419 for 'es' > en-US for 'en'
									if (baseLanguage === 'es') {
										// Prefer es-ES, fall back to es-419
										return (
											feedbackType.titleTranslations['es-ES'] ||
											feedbackType.titleTranslations['es-419'] ||
											feedbackType.titleTranslations['en-US'] ||
											feedbackType.name
										)
									} else if (baseLanguage === 'en') {
										return (
											feedbackType.titleTranslations['en-US'] ||
											feedbackType.name
										)
									}

									// Fall back to English if no match
									return (
										feedbackType.titleTranslations['en-US'] || feedbackType.name
									)
								}

								return (
									<div
										key={feedbackType.id}
										className="flex items-center space-x-3"
									>
										<RadioGroupItem
											value={feedbackType.id.toString()}
											id={`feedback-${feedbackType.id}`}
										/>
										<Label
											htmlFor={`feedback-${feedbackType.id}`}
											className="flex-1 cursor-pointer text-sm font-normal leading-normal"
										>
											{getTranslatedTitle()}
										</Label>
									</div>
								)
							})}
						</RadioGroup>
					)}
				</div>
				<div className="flex flex-col-reverse md:flex-row md:flex-reverse justify-end pt-4 border-t gap-2 h-auto md:h-[72px]">
					<Button
						variant="outline"
						onClick={() => {
							onClose()
							setSelectedNegativeFeedbackTypeId(null)
						}}
						className="rounded-full"
					>
						{t('actions.cancel')}
					</Button>
					<Button
						variant="default"
						onClick={() => {
							onConfirm(selectedNegativeFeedbackTypeId || 0)
						}}
						disabled={!selectedNegativeFeedbackTypeId}
						className="rounded-full"
					>
						{t('entities.music.send_feedback')}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	)
}
